import { UpgradeAndConsentDialog } from '@components/UpgradeAndConsentDialog/V2/UpgradeAndConsentDialog';
import { UpgradeAndConsentFlow } from '@components/UpgradeAndConsentDialog/V2/UpgradeAndConsentFlow';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '@modules/feature-flag';

/**
 * Enum representing the types of modals that can be opened.
 * Add modals here if you need them globally accessible.
 */
export enum ModalType {
  UPGRADE = 'UPGRADE',
}

/**
 * Current modal state
 */
type CurrentModalState = {
  type: ModalType;
  trackingSource?: string;
};

/**
 * Context value type for managing global modals.
 */
type GlobalModalContext = {
  currentModal: ModalType | null;
  openModal: (props: CurrentModalState) => void;
  closeModal: () => void;
  registerModals: (registeredModals: ModalType[]) => void;
};

/**
 * Default context values.
 */
export const GlobalModalContext = createContext<GlobalModalContext>({
  currentModal: null,
  openModal: () => {
    // noop
  },
  registerModals: () => {
    // noop
  },
  closeModal: () => {
    // noop
  },
});

/**
 * Provider component for the GlobalModal context.
 */
export const GlobalModalProvider = ({ children }: { children: ReactNode }) => {
  const [currentModal, setCurrentModal] = useState<CurrentModalState | null>(null);
  const registeredModals = useRef<Set<ModalType>>(new Set());

  const registerModals = (newModals: ModalType[]) => {
    newModals.forEach((modal) => {
      registeredModals.current.add(modal);
    });
  };

  return (
    <GlobalModalContext.Provider
      value={{
        currentModal: currentModal?.type || null,
        registerModals,
        openModal: setCurrentModal,
        closeModal: () => {
          setCurrentModal(null);
        },
      }}
    >
      {children}
      <div>
        {registeredModals.current.has(ModalType.UPGRADE) && (
          <UpgradeAndConsentDialog
            type={'upgrade'}
            show={currentModal?.type === ModalType.UPGRADE}
            trackingSource={currentModal?.trackingSource}
            onClose={() => setCurrentModal(null)}
          />
        )}
        {/* Auto opening dialog, does not render itself unless needed */}
        <UpgradeAndConsentFlow />
      </div>
    </GlobalModalContext.Provider>
  );
};

/**
 * Props required for the useGlobalModal hook.
 */
type GlobalModalProps<T extends ModalType> = {
  trackingSource?: string;
  registeredModals: T[];
};

/**
 * Return type for the useGlobalModal hook.
 */
type GlobalModalReturn<T extends ModalType> = {
  openModal: (type: T) => void;
  closeModal: () => void;
  currentModal: ModalType | null;
};

/**
 * Hook to use the GlobalModal context in functional components.
 *
 * @param props - The props required to configure the modal context.
 * Tracking source is used in some modals to log modal open locations.
 * Registered modals is an array of modal types that are available to open - only these will render on the page.
 * @returns An object with methods to open and close modals and the current modal state.
 */
export const useGlobalModal = <T extends ModalType>({
  trackingSource,
  registeredModals,
}: GlobalModalProps<T>): GlobalModalReturn<T> => {
  const { openModal, closeModal, registerModals, currentModal } = useContext(GlobalModalContext);

  useEffect(() => {
    registerModals(registeredModals);
  }, [registeredModals, registerModals]);

  const openTypedModal = (type: T) => {
    if (registeredModals.includes(type)) {
      openModal({ type, trackingSource });
    } else {
      console.warn(`Modal type ${type} is not registered for this hook instance.`);
    }
  };

  return {
    currentModal,
    openModal: openTypedModal,
    closeModal,
  };
};

/**
 * Example usage in a component:
 *
 * import React from 'react';
 * import { useGlobalModal, ModalType } from './path/to/GlobalModalContext';
 *
 * const ExampleComponent: React.FC = () => {
 *   const { openModal } = useGlobalModal({
 *     trackingSource: 'example',
 *     registeredModals: [ModalType.UPGRADE]
 *   });
 *
 *   const handleOpenModal = () => {
 *     openModal(ModalType.UPGRADE);
 *   };
 *
 *   return (
 *     <button onClick={handleOpenModal}>
 *       Open Upgrade Modal
 *     </button>
 *   );
 * };
 */
